import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// One-time cleanup: dismiss suppression is now server-authoritative (the silence
// endpoint, ~2h personal). The card no longer persists `dismissedBucketEnd`, so the
// legacy persisted key is dead — clear it to avoid orphaned localStorage state.
if (typeof localStorage !== 'undefined') localStorage.removeItem('risk-recognition');

export const RISK_RECOGNITION_RULE_NAMES = ['long_journey', 'severe_speed', 'fatigue'] as const;
export type RiskRecognitionRuleName = (typeof RISK_RECOGNITION_RULE_NAMES)[number];

export const isKnownRiskRule = (ruleName: string | undefined): ruleName is RiskRecognitionRuleName =>
  !!ruleName && (RISK_RECOGNITION_RULE_NAMES as readonly string[]).includes(ruleName);

// Accepts `undefined` too: the highlight now comes from the query cache (`data` is `T | undefined`).
export const isHighlightRenderable = (highlight: RiskRecognitionHighlight | null | undefined, dismissedBucketEnd: string | null): boolean =>
  !!highlight && highlight.detection_count > 0 && isKnownRiskRule(highlight.metadata?.rule_name) && highlight.time_bucket_end !== dismissedBucketEnd;

export const buildRiskRecognitionEventProps = (highlight: RiskRecognitionHighlight | null | undefined) => {
  const metadata = highlight?.metadata;
  return {
    rule_name: metadata?.rule_name,
    criticality_score: metadata?.criticality_score,
    device_id: metadata?.device_id,
    vehicle_plate: metadata?.vehicle_plate,
    driver_id: metadata?.driver_id,
    time_bucket_begin: highlight?.time_bucket_begin,
  };
};

interface RiskRecognitionState {
  lastDisplayedBucketEnd: string | null;
  dismissedBucketEnd: string | null;
  /** Bucket the user chose to follow onto /tracking via "Ver trajeto"; null when not following. In-memory only. */
  routeFollowBucketEnd: string | null;
  isDrawerOpen: boolean;
  markDisplayed: (bucketEnd: string) => void;
  // The highlight is server state (query cache), so callers pass the acted-on bucket end in.
  dismiss: (bucketEnd: string | null) => void;
  followRouteToTracking: (bucketEnd: string | null) => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  reset: () => void;
}

// `dismissedBucketEnd` is in-memory only: the server (silence endpoint) is the
// source of truth for suppression. It still gates re-render within a session so the
// card stays hidden after the exit animation settles; a reload may re-show it until
// the next ~10-min window reflects the silence.
const useRiskRecognition = create<RiskRecognitionState, [['zustand/devtools', never]]>(
  devtools(
    (set) => ({
      lastDisplayedBucketEnd: null,
      dismissedBucketEnd: null,
      routeFollowBucketEnd: null,
      isDrawerOpen: false,
      markDisplayed: (bucketEnd) => set({ lastDisplayedBucketEnd: bucketEnd }, false, 'markDisplayed'),
      dismiss: (bucketEnd) => set({ dismissedBucketEnd: bucketEnd, routeFollowBucketEnd: null }, false, 'dismiss'),
      followRouteToTracking: (bucketEnd) => set({ routeFollowBucketEnd: bucketEnd }, false, 'followRouteToTracking'),
      openDrawer: () => set({ isDrawerOpen: true }, false, 'openDrawer'),
      closeDrawer: () => set({ isDrawerOpen: false }, false, 'closeDrawer'),
      reset: () => set({ lastDisplayedBucketEnd: null, dismissedBucketEnd: null, routeFollowBucketEnd: null, isDrawerOpen: false }, false, 'reset'),
    }),
    { name: 'useRiskRecognition' }
  )
);

export default useRiskRecognition;
